/**
 * Speech-coach streak engine — pure client-side port of
 * plugins/speech-coach/server/streak.ts.
 *
 * WHY DUPLICATE: the chassis rule prohibits client code from importing server
 * modules (would drag Drizzle + postgres.js into the browser bundle via
 * transitive imports). This module is genuinely pure — no DB, no env, no Node
 * builtins — so the 40-line duplication is stable and safe.
 *
 * Keep in sync with server/streak.ts when the algorithm changes (including the grace rule).
 */

/** Days since the Unix epoch for a "YYYY-MM-DD" calendar date (UTC midnight). */
function ordinal(localDate: string): number {
	const [y, m, d] = localDate.split("-").map(Number) as [number, number, number];
	return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Whole-day gap between two calendar dates: ordinal(a) − ordinal(b). */
function dayGap(a: string, b: string): number {
	return ordinal(a) - ordinal(b);
}

export type StreakStats = {
	/** Length of the consecutive run ending at the most recent active day, IF that
	 *  day is today or yesterday (otherwise the streak has lapsed → 0). */
	current: number;
	/** Longest consecutive run anywhere in history. */
	longest: number;
	/** Whether a session has already been logged for `today`. */
	todayCounts: boolean;
	/** Most recent active local date, or null if there's no activity yet. */
	lastActiveDate: string | null;
	/** Count of distinct active days across all history. */
	totalActiveDays: number;
};

/**
 * Compute streak stats from a list of active local dates (duplicates and order
 * don't matter — two sessions on one day collapse to a single streak day) and
 * the current local date.
 */
export function computeStreakStats(localDates: string[], today: string): StreakStats {
	const distinct = [...new Set(localDates)].sort();
	const total = distinct.length;
	if (total === 0) {
		return { current: 0, longest: 0, todayCounts: false, lastActiveDate: null, totalActiveDays: 0 };
	}

	// Longest consecutive run anywhere in history.
	let longest = 1;
	let run = 1;
	for (let i = 1; i < distinct.length; i++) {
		// biome-ignore lint/style/noNonNullAssertion: i and i-1 are in-bounds
		run = dayGap(distinct[i]!, distinct[i - 1]!) === 1 ? run + 1 : 1;
		if (run > longest) longest = run;
	}

	// biome-ignore lint/style/noNonNullAssertion: total > 0 guaranteed above
	const lastActive = distinct[total - 1]!;

	// Anchor the current run on the most recent day <= today (future-dated entries
	// must not keep a lapsed streak alive). The streak is "alive" only if that
	// anchor is today or yesterday.
	//
	// Grace rule: at most 1 missed day is forgiven per rolling 7-day window.
	// - gap === 1 → consecutive, run continues.
	// - gap === 2 → forgive if a skip budget is available in the current window;
	//   run continues (the forgiven gap day is NOT itself counted). Budget consumed.
	//   If the current position is > 7 days before the last forgiven skip, the
	//   rolling window resets (budget restored to 1). If no budget left → break.
	// - gap >= 3 → always break; never forgiven.
	const upToToday = distinct.filter((d) => dayGap(today, d) >= 0);
	let current = 0;
	if (upToToday.length > 0) {
		// biome-ignore lint/style/noNonNullAssertion: length > 0 guaranteed above
		const anchor = upToToday[upToToday.length - 1]!;
		const anchorGap = dayGap(today, anchor);
		if (anchorGap <= 1 || anchorGap === 2) {
			current = 1;
			let skipsRemaining = 1;
			let lastForgiven: number | null = null;
			// If the anchor itself is 2 days before today (one missed day between
			// the anchor and today), consume a skip for that gap right away.
			if (anchorGap === 2) {
				skipsRemaining -= 1;
				lastForgiven = ordinal(today) - 1;
			}
			for (let i = upToToday.length - 1; i > 0; i--) {
				// biome-ignore lint/style/noNonNullAssertion: i and i-1 are in-bounds
				const gap = dayGap(upToToday[i]!, upToToday[i - 1]!);
				if (gap === 1) {
					current += 1;
				} else if (gap === 2) {
					// biome-ignore lint/style/noNonNullAssertion: i is in-bounds
					const laterOrd = ordinal(upToToday[i]!);
					if (lastForgiven !== null && lastForgiven - laterOrd > 7) {
						skipsRemaining = 1;
					}
					if (skipsRemaining > 0) {
						current += 1;
						skipsRemaining -= 1;
						lastForgiven = laterOrd - 1;
					} else {
						break;
					}
				} else {
					break;
				}
			}
		}
	}

	return {
		current,
		longest: Math.max(longest, current),
		todayCounts: new Set(distinct).has(today),
		lastActiveDate: lastActive,
		totalActiveDays: total,
	};
}
